import React, { useContext, useMemo, useState } from "react";
import AuthContext from "../AuthContext";
import { apiFetch } from "../api/client";
import { useNotification } from "../components/Notifications";
import { mapApiErrorToNotification } from "../utils/notificationHelpers";

function splitFullName(fullName) {
    const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
    return {
        firstName: parts[0] || "",
        lastName: parts.slice(1).join(" "),
    };
}

function normalizeInternationalPhone(value) {
    const cleaned = String(value || "").replace(/[^\d+]/g, "");
    const plus = cleaned.startsWith("+") ? "+" : "";
    return plus + cleaned.replace(/\+/g, "").slice(0, 15);
}

function isValidInternationalPhone(value) {
    if (!value) return true;
    return /^\+[1-9]\d{7,14}$/.test(value);
}

function Profile() {
    const auth = useContext(AuthContext);
    const notify = useNotification();
    const storedUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("user") || "{}") || {};
        } catch {
            return {};
        }
    }, []);

    const currentUser = auth?.user || storedUser || {};
    const currentFullName = [currentUser?.firstName || currentUser?.FirstName, currentUser?.lastName || currentUser?.LastName]
        .filter(Boolean)
        .join(" ");

    const [form, setForm] = useState({
        fullName: currentUser?.fullName || currentUser?.FullName || currentFullName || "",
        phoneNumber: currentUser?.phoneNumber || currentUser?.PhoneNumber || "",
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (field, value) => {
        setForm((prev) => ({
            ...prev,
            [field]: field === "phoneNumber" ? normalizeInternationalPhone(value) : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const trimmedName = form.fullName.trim();
        if (!trimmedName) {
            notify("error", { title: "Validation", text: "Full name is required." });
            return;
        }

        const phoneNumber = form.phoneNumber.trim();
        if (!isValidInternationalPhone(phoneNumber)) {
            notify("error", { title: "Validation", text: "Phone number must start with a country code, for example +2348032466818." });
            return;
        }

        const { firstName, lastName } = splitFullName(trimmedName);
        setLoading(true);

        try {
            const res = await apiFetch(`/api/account/profile`, {
                method: "PUT",
                body: JSON.stringify({
                    FirstName: firstName,
                    LastName: lastName,
                    PhoneNumber: phoneNumber,
                }),
            });

            const data = await res.json().catch(() => null);
            if (!res.ok || data?.succeeded === false) {
                notify("error", mapApiErrorToNotification(data, res.status));
                return;
            }

            const updatedUser = {
                ...currentUser,
                firstName,
                lastName,
                fullName: trimmedName,
                phoneNumber,
            };

            localStorage.setItem("user", JSON.stringify(updatedUser));
            if (auth?.signin) {
                auth.signin(updatedUser);
            }

            notify("success", { title: "Profile updated", text: "Your profile details have been saved." });
        } catch (err) {
            console.error(err);
            notify("error", { title: "Network error", text: "Could not reach server." });
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="col-span-12 min-h-screen px-4 py-6 sm:px-6 lg:col-span-10 lg:px-8">
            <div className="mx-auto w-full max-w-4xl">
                <div className="mb-6">
                    <p className="text-sm font-semibold uppercase tracking-wide text-[#D326AD]">Account</p>
                    <h1 className="mt-2 text-3xl font-bold text-[#E6EEF3]">Update Profile</h1>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-[#C7D2FE]">
                        Keep your personal details up to date. Your business and login details are managed separately.
                    </p>
                </div>

                <div className="rounded-2xl border border-[#8A17D1]/30 bg-[#111B31] p-6 shadow-xl shadow-[#020617]/30 sm:p-8">
                    <div className="mb-6 rounded-xl border border-[#C7D2FE]/10 bg-[#0B1220] p-4">
                        <h2 className="text-lg font-semibold text-[#E6EEF3]">Personal Information</h2>
                        <p className="mt-1 text-sm text-[#C7D2FE]">Only your full name and phone number can be changed here.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="grid gap-5 md:grid-cols-2">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-[#E6EEF3]" htmlFor="fullName">
                                    Full Name
                                </label>
                                <input
                                    id="fullName"
                                    type="text"
                                    value={form.fullName}
                                    onChange={(e) => handleChange("fullName", e.target.value)}
                                    placeholder="Enter your full name"
                                    disabled={loading}
                                    className="block w-full rounded-lg border border-[#C7D2FE]/15 bg-[#0B1220] px-4 py-3 text-[#E6EEF3] outline-none transition placeholder:text-[#A5B4D8] focus:border-[#8A17D1] focus:ring-2 focus:ring-[#8A17D1]/25"
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-[#E6EEF3]" htmlFor="phoneNumber">
                                    Phone Number
                                </label>
                                <input
                                    id="phoneNumber"
                                    type="tel"
                                    inputMode="tel"
                                    pattern="^\+[1-9]\d{7,14}$"
                                    value={form.phoneNumber}
                                    onChange={(e) => handleChange("phoneNumber", e.target.value)}
                                    placeholder="+2348032466818"
                                    disabled={loading}
                                    className="block w-full rounded-lg border border-[#C7D2FE]/15 bg-[#0B1220] px-4 py-3 text-[#E6EEF3] outline-none transition placeholder:text-[#A5B4D8] focus:border-[#8A17D1] focus:ring-2 focus:ring-[#8A17D1]/25"
                                />
                                <p className="mt-2 text-xs text-[#C7D2FE]">Use international format with country code, e.g. +2348032466818.</p>
                            </div>
                        </div>

                        <div className="flex justify-end pt-2">
                            <button
                                type="submit"
                                disabled={loading}
                                className="rounded-lg bg-gradient-to-r from-[#8A17D1] to-[#D326AD] px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {loading ? "Saving..." : "Save Profile"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </main>
    );
}

export default Profile;
